import Markdown, { MarkdownOptions } from 'markdown-to-jsx';
import React from 'react';

// Model output is untrusted. Escaping '<' makes any raw HTML in a response render as text
// instead of elements (this version of markdown-to-jsx can't disable HTML parsing).
const escapeHtml = (text: string): string => text.replace(/</g, '&lt;');

const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s/;

// This version of markdown-to-jsx only starts a list after a blank line, but the model often
// writes a list directly under a line of text (e.g. "**Step 1**" then "- Sign in"), as CommonMark allows.
const separateLists = (text: string): string =>
    text
        .split('\n')
        .map((line, index, lines) => {
            const previous = index > 0 ? lines[index - 1] : '';
            const startsList = LIST_ITEM.test(line) && previous.trim() !== '' && !LIST_ITEM.test(previous);
            return startsList ? `\n${line}` : line;
        })
        .join('\n');

// Headings are scaled down to fit the chat bubble.
const Heading: React.FC = ({ children }) => <p style={{ fontWeight: 600, margin: '10px 0 4px' }}>{children}</p>;

const MARKDOWN_OPTIONS: MarkdownOptions = {
    forceBlock: true,
    overrides: {
        h1: { component: Heading },
        h2: { component: Heading },
        h3: { component: Heading },
        h4: { component: Heading },
        h5: { component: Heading },
        h6: { component: Heading },
        p: { component: 'p', props: { style: { margin: '0 0 8px' } } },
        ul: { component: 'ul', props: { style: { margin: '0 0 8px', paddingLeft: 20 } } },
        ol: { component: 'ol', props: { style: { margin: '0 0 8px', paddingLeft: 20 } } },
        li: { component: 'li', props: { style: { marginBottom: 2 } } },
        a: { component: 'a', props: { target: '_blank', rel: 'noopener noreferrer' } },
        code: {
            component: 'code',
            props: {
                style: {
                    fontFamily: 'monospace',
                    fontSize: 13,
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    padding: '0 3px',
                    borderRadius: 3,
                },
            },
        },
    },
};

interface AriaMessageContentProps {
    content: string;
}

/**
 * Renders an assistant response, which the model writes as Markdown.
 */
const AriaMessageContent: React.FC<AriaMessageContentProps> = ({ content }) => (
    <Markdown options={MARKDOWN_OPTIONS}>{separateLists(escapeHtml(content.trim()))}</Markdown>
);

export default AriaMessageContent;
